
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * NotificationTemplate struct
 */
class NotificationTemplate {
  /**
   * Initialize struct
   * @param {!string=} title
   * @param {!string=} body
   * @constructor
   */
  constructor (title = '', body = '') {
    this.Title = title
    this.Body = body
  }

  /**
   * Copy struct (shallow copy)
   * @this {!NotificationTemplate}
   * @param {!NotificationTemplate} other Other struct
   * @returns {!NotificationTemplate} This struct
   */
  copy (other) {
    if (other.Title != null) {
      this.Title = other.Title
    } else {
      this.Title = null
    }
    if (other.Body != null) {
      this.Body = other.Body
    } else {
      this.Body = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!NotificationTemplate}
   * @returns {!NotificationTemplate} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new NotificationTemplateModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new NotificationTemplateModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!NotificationTemplate}
   * @param {!NotificationTemplate} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof NotificationTemplate)) {
      throw new TypeError('Instance of NotificationTemplate is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!NotificationTemplate}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Title: ((this.Title != null) ? this.Title : null),
      Body: ((this.Body != null) ? this.Body : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return NotificationTemplate.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!NotificationTemplate} other Object value
   * @returns {!NotificationTemplate} Created struct
   */
  static fromObject (other) {
    return new NotificationTemplate().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!NotificationTemplate}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return NotificationTemplate.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!NotificationTemplate}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 212
  }
}

export { NotificationTemplate };

/**
 * Fast Binary Encoding NotificationTemplate field model
 */
class FieldModelNotificationTemplate extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Title = new fbe.FieldModelString(buffer, 4 + 4)
    this.Body = new fbe.FieldModelString(buffer, this.Title.fbeOffset + this.Title.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelNotificationTemplate}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelNotificationTemplate}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Title.fbeSize + this.Body.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelNotificationTemplate}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Title.fbeExtra + this.Body.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelNotificationTemplate}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelNotificationTemplate.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelNotificationTemplate}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 212
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelNotificationTemplate}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelNotificationTemplate}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Title.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Title.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Title.fbeSize

    if ((fbeCurrentSize + this.Body.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Body.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Body.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelNotificationTemplate}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelNotificationTemplate}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelNotificationTemplate}
   * @param {!NotificationTemplate} fbeValue Default value, defaults is new NotificationTemplate()
   * @returns {!NotificationTemplate} NotificationTemplate value
   */
  get (fbeValue = new NotificationTemplate()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelNotificationTemplate}
   * @param {!NotificationTemplate} fbeValue NotificationTemplate value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Title.fbeSize) <= fbeStructSize) {
      fbeValue.Title = this.Title.get()
    } else {
      fbeValue.Title = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Title.fbeSize

    if ((fbeCurrentSize + this.Body.fbeSize) <= fbeStructSize) {
      fbeValue.Body = this.Body.get()
    } else {
      fbeValue.Body = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Body.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelNotificationTemplate}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelNotificationTemplate}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelNotificationTemplate}
   * @param {!NotificationTemplate} fbeValue NotificationTemplate value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelNotificationTemplate}
   * @param {NotificationTemplate} fbeValue NotificationTemplate value
   */
  setFields (fbeValue) {
    this.Title.set(fbeValue.Title)
    this.Body.set(fbeValue.Body)
  }
}

export { FieldModelNotificationTemplate };

/**
 * Fast Binary Encoding NotificationTemplate model
 */
class NotificationTemplateModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelNotificationTemplate(this.buffer, 4)
  }

  /**
   * Get the NotificationTemplate model
   * @this {!NotificationTemplateModel}
   * @returns {!FieldModelNotificationTemplate} model NotificationTemplate model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!NotificationTemplateModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!NotificationTemplateModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return NotificationTemplateModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!NotificationTemplateModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelNotificationTemplate.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!NotificationTemplateModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!NotificationTemplateModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!NotificationTemplateModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!NotificationTemplateModel}
   * @param {!NotificationTemplate} value NotificationTemplate value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!NotificationTemplateModel}
   * @param {!NotificationTemplate} value NotificationTemplate value, defaults is new NotificationTemplate()
   * @return {!object} Deserialized NotificationTemplate value and its size
   */
  deserialize (value = new NotificationTemplate()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new NotificationTemplate(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new NotificationTemplate(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!NotificationTemplateModel}
   * @param {!number} prev Previous NotificationTemplate model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { NotificationTemplateModel };
